import {
  Badge,
  Box,
  Link,
  Flex,
  HStack,
  Text,
  VStack,
  useColorMode,
  Stack,
  Spinner,
} from '@chakra-ui/react'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import NextLink from 'next/link'
import { BsFillPatchCheckFill } from 'react-icons/bs'
import { otcDealOverviewType, raffleType } from '../../../techRaffles/types'
import { ProfilePicture } from '../ProfilePicture'
import { formatFloatForDisplay } from '../../../../utils/utils'
import { UseOnChainRaffleType } from '../../../techRaffles/hooks/hookTypes'
import { useMemo } from 'react'

export const NFTNameInfoDetail = ({
  isRaffle,
  raffle,
  onChainRaffleRes,
  otcDeal,
}: {
  isRaffle?: boolean
  raffle?: raffleType
  onChainRaffleRes?: UseOnChainRaffleType
  otcDeal?: otcDealOverviewType
}) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'

  const { name, collection, creatorUser, creatorProject } =
    (isRaffle ? raffle : otcDeal) ?? ({} as any)

  const creatorUserAddress = useMemo(() => {
    if (!creatorUser) {
      return ''
    }
    if (creatorUser.name.length < 26) {
      return creatorUser.name
    }
    return creatorUser.name.slice(0, 8) + '..' + creatorUser.name.slice(-8)
  }, [creatorUser])

  if (!raffle && !otcDeal) {
    return <Spinner color={isDarkMode ? 'white' : 'black'} />
  }

  const type = isRaffle ? raffle?.type : 'NFT'

  const isRewardRaffle = isRaffle && raffle?.type === 'REWARD'

  return (
    <Box py={['1rem', '1rem', 0]}>
      <VStack alignItems={['center', 'center', 'flex-start']}>
        <Text fontSize={['1.75rem', '2rem']} fontWeight='600'>
          {name}
        </Text>
        {type === 'NFT' && (
          <Box fontSize='0.875rem' color={isDarkMode ? '#d4d4d4' : '##C1C1C1'}>
            Collection{' '}
            <NextLink href={`/collections/${collection?.name}`} passHref>
              <Link
                fontSize='.875rem'
                color={isDarkMode ? 'white' : 'black'}
                textDecoration='underline'
              >
                {collection?.title ?? 'Unknown'}
              </Link>
            </NextLink>
            <Stack gap={''} mt='.4rem' direction={['column', 'row']}>
              {collection?.averagePrice24hr ? (
                <>
                  <Flex alignItems={'center'} justifyContent='center'>
                    <i style={{ marginRight: '4px' }}>AVG. SALE (24h):</i>{' '}
                    {formatFloatForDisplay(
                      collection?.averagePrice24hr / LAMPORTS_PER_SOL
                    )}
                    &#9678;
                    {/*<Image
                      ml={1}
                      display='inline'
                      src='/icons/solanaLogoMark.svg'
                      w='12px'
                      h='100%'
                    ></Image>*/}
                  </Flex>
                </>
              ) : (
                ''
              )}
              {collection?.floorPrice ? (
                <>
                  <Text display={['none', 'unset']}>-</Text>
                  <Flex alignItems={'center'} justifyContent='center'>
                    <i style={{ marginRight: '4px' }}>Floor Price (ME):</i>{' '}
                    {formatFloatForDisplay(
                      collection?.floorPrice / LAMPORTS_PER_SOL
                    )}
                    &#9678;
                    {/*<Image
                      ml={1}
                      display='inline'
                      src='/icons/solanaLogoMark.svg'
                      w='12px'
                      h='100%'
                    ></Image>*/}
                  </Flex>
                </>
              ) : (
                ''
              )}
              {isRaffle &&
              raffle?.maxTickets &&
              raffle?.estimateTicketPriceInSol ? (
                <>
                  <Text display={['none', 'unset']}>-</Text>
                  <Flex alignItems={'center'} justifyContent='center'>
                    <i style={{ marginRight: '4px' }}>Total ticket value:</i>{' '}
                    {formatFloatForDisplay(
                      raffle.maxTickets * raffle.estimateTicketPriceInSol
                    )}
                    &#9678;
                    {/*<Image
                      ml={1}
                      display='inline'
                      src='/icons/solanaLogoMark.svg'
                      w='12px'
                      h='100%'
                    ></Image>*/}
                  </Flex>
                </>
              ) : (
                ''
              )}

              {isRaffle &&
              onChainRaffleRes?.data &&
              raffle?.estimateTicketPriceInSol ? (
                <>
                  <Text display={['none', 'unset']}>-</Text>
                  <Flex alignItems={'center'} justifyContent='center'>
                    <i style={{ marginRight: '4px' }}>Ticket volume:</i>{' '}
                    {formatFloatForDisplay(
                      onChainRaffleRes.data.ticketCount *
                        raffle?.estimateTicketPriceInSol
                    )}
                    &#9678;
                    {/*<Image
                      ml={1}
                      display='inline'
                      src='/icons/solanaLogoMark.svg'
                      w='12px'
                      h='100%'
                    ></Image>*/}
                  </Flex>
                </>
              ) : (
                ''
              )}
            </Stack>
          </Box>
        )}
        {type === 'WHITELIST' && (
          <HStack>
            <Badge paddingY={1} paddingX={2} size='sm' rounded='full'>
              Whitelist Raffle
            </Badge>
            <Badge paddingY={1} paddingX={2} size='sm' rounded='full'>
              {raffle?.wlSpots} spots
            </Badge>
          </HStack>
        )}

        {type === 'IRL' && raffle?.winnerSpots && (
          <HStack>
            <Badge paddingY={1} paddingX={2} size='sm' rounded='full'>
              IRL Raffle
            </Badge>
            {raffle.winnerSpots && (
              <Badge paddingY={1} paddingX={2} size='sm' rounded='full'>
                {raffle?.winnerSpots} winner
                {raffle?.winnerSpots > 1 ? 's' : ''}
              </Badge>
            )}
          </HStack>
        )}

        {!isRewardRaffle && (
          <Flex
            gap='4rem'
            mt={['1rem!important', '1rem!important', '2rem!important']}
          >
            <HStack>
              {creatorProject && (
                <ProfilePicture
                  gradientstart={creatorProject.gradientStart}
                  gradientend={creatorProject.gradientEnd}
                  w='2.5rem'
                  h='2.5rem'
                  rounded='full'
                  imageUrl={creatorProject.profilePictureUrl}
                />
              )}
              {creatorUser && (
                <ProfilePicture
                  gradientstart={creatorUser.gradientStart}
                  gradientend={creatorUser.gradientEnd}
                  w='2.5rem'
                  h='2.5rem'
                  rounded='full'
                  imageUrl={creatorUser.profilePictureUrl}
                />
              )}
              <VStack align='start' spacing={0}>
                <Text fontSize='0.875rem' color='#BDBDBD' textAlign='left'>
                  Created by
                </Text>
                <HStack>
                  {creatorProject && (
                    <NextLink href={`/p/${creatorProject.publicId}`} passHref>
                      <Link>
                        <Text fontSize='0.875rem'>
                          @{creatorProject?.communityName}
                        </Text>
                      </Link>
                    </NextLink>
                  )}
                  {creatorUser && (
                    <NextLink
                      href={`/u/${creatorUser.wallet}`}
                      target='_blank'
                      passHref
                    >
                      <Link target='_blank'>
                        <Text fontSize='0.875rem'>@{creatorUserAddress}</Text>
                      </Link>
                    </NextLink>
                  )}

                  {creatorProject?.verified && <BsFillPatchCheckFill />}
                </HStack>
              </VStack>
            </HStack>
          </Flex>
        )}
      </VStack>
    </Box>
  )
}
